import { buildCardExpiration } from '@subscriptions/application/mappers';
import type { SubscriptionBlock } from '@subscriptions/domain/entities';
import { createSubscriptionBlock } from '@subscriptions/domain/entities';
import { CustomerStates, PaymentMethodTypes, SubscriptionBlockSubStatuses } from '@subscriptions/domain/types';
import type {
  CreateSubscriptionBlockDto,
  UpdateBlockPaymentMethodDto,
  UpdateBlockInvoiceDataDto,
  UpdateBlockStoresDto,
  StoreViewDto,
  PaymentMethodViewDto,
} from '@subscriptions/application/dtos';
import type {
  SubscriptionBlockApiResponse,
  CreateSubscriptionBlockApiRequest,
  UpdateBlockPaymentMethodApiRequest,
  UpdateBlockInvoiceDataApiRequest,
  UpdateBlockStoresApiRequest,
  StoreApiResponse,
  PaymentMethodApiResponse,
} from '@subscriptions/infrastructure/api/types';

// Shared translation of the canonical `providerPublic*` payment-method shape
// (generate-reference and attach-payment-method return it too).
import { mapPaymentMethodApiResponseToViewDto } from './PaymentMethodApiMapper';

/** Mapper type for subscription block API transformations */
export type SubscriptionBlockApiMapperType = {
  mapApiResponseToEntity: (response: SubscriptionBlockApiResponse) => SubscriptionBlock;
  mapCreateDtoToApiRequest: (dto: CreateSubscriptionBlockDto) => CreateSubscriptionBlockApiRequest;
  mapUpdatePaymentMethodDtoToApiRequest: (dto: UpdateBlockPaymentMethodDto) => UpdateBlockPaymentMethodApiRequest;
  mapUpdateInvoiceDataDtoToApiRequest: (dto: UpdateBlockInvoiceDataDto) => UpdateBlockInvoiceDataApiRequest;
  mapUpdateStoresDtoToApiRequest: (dto: UpdateBlockStoresDto) => UpdateBlockStoresApiRequest;
  mapStoresApiToViewDtos: (apiResponses: StoreApiResponse[]) => StoreViewDto[];
  mapPaymentMethodApiResponseToViewDto: (response: PaymentMethodApiResponse) => PaymentMethodViewDto;
};

/**
 * Creates mapper for subscription block API type conversions.
 *
 * Translates backend-specific shapes to domain vocabulary:
 * - `invoiceData.taxId` → `rfc`, `invoiceData.taxInvoiceUse` → `cfdi`
 * - `paymentMethod.providerPublicId` is the CLABE for SPEI and the last-four for CARD
 * - CARD details come from the canonical `providerPublic*` keys:
 *   `brand` ← `providerPublicBrand`, `expirationDate` ← `providerPublicMonth`/`providerPublicYear`,
 *   `lastFourDigits` ← `providerPublicId`
 * - `stores[].storeUuid` passes through as `storeUuid`
 */
export const createSubscriptionBlockApiMapper = (): SubscriptionBlockApiMapperType => {
  // Read flow: soft validation so one corrupt field/block degrades to empty values instead of breaking the render
  const mapApiResponseToEntity = (response: SubscriptionBlockApiResponse): SubscriptionBlock => {
    const paymentMethod = response.paymentMethod;
    // Card-only fields stay null for SPEI: its providerPublicId is the CLABE (surfaced via
    // `providerPublicId`), never a last-four — mirrors the contract in mapToPaymentMethodViewDto.
    const isCard = paymentMethod?.methodType === PaymentMethodTypes.CARD;

    return createSubscriptionBlock(
      {
        uuid: response.uuid,
        name: response.name,
        extensionPos: response.extensionPos,
        extensionConnect: response.extensionConnect,
        total: response.total,
        paymentDate: response.paymentDate ?? '',
        interval: response.interval,
        status: response.status,
        subStatus: response.subStatus ?? SubscriptionBlockSubStatuses.NONE,
        customerState: response.customerState ?? CustomerStates.ACTIVE,
        inactiveSince: response.inactiveSince ?? null,
        advanceInvoice: response.advanceInvoice ?? false,
        groupInvoice: response.groupInvoice ?? false,
        configEnabled: response.configEnabled ?? true,
        paymentMethod: paymentMethod
          ? {
              uuid: paymentMethod.uuid,
              provider: paymentMethod.provider,
              providerId: paymentMethod.providerId ?? null,
              providerPublicId: paymentMethod.providerPublicId ?? '',
              methodType: paymentMethod.methodType,
              orgUuid: paymentMethod.orgUuid,
              brand: isCard ? paymentMethod.providerPublicBrand ?? null : null,
              expirationDate: isCard
                ? buildCardExpiration(paymentMethod.providerPublicMonth, paymentMethod.providerPublicYear)
                : null,
              lastFourDigits: isCard ? paymentMethod.providerPublicId ?? null : null,
              status: paymentMethod.status,
              reactivation: paymentMethod.reactivation,
            }
          : null,
        invoiceData: response.invoiceData
          ? {
              uuid: response.invoiceData.uuid,
              orgUuid: response.invoiceData.orgUuid,
              businessName: response.invoiceData.businessName ?? '',
              firstName: response.invoiceData.firstName ?? '',
              firstLastName: response.invoiceData.firstLastName ?? '',
              secondLastName: response.invoiceData.secondLastName ?? '',
              postalCode: response.invoiceData.postalCode,
              rfc: response.invoiceData.taxId,
              cfdi: response.invoiceData.taxInvoiceUse,
              email: response.invoiceData.email,
              taxRegime: response.invoiceData.taxRegime,
            }
          : null,
        lastError: response.lastError ?? null,
        discounts: response.discounts ?? null,
        stores: response.stores.map((s) => ({
          storeUuid: s.storeUuid,
          storeName: s.storeName ?? '',
          plan: s.plan ?? '',
          churnedAt: s.churnedAt ?? null,
          concepts: (s.concepts ?? []).map((c) => ({
            key: c.key,
            value: c.value,
            name: c.name ?? null,
            description: c.description ?? null,
            quantity: c.quantity ?? null,
            periodStart: c.periodStart ?? null,
            periodEnd: c.periodEnd ?? null,
          })),
        })),
      },
      { softValidation: true },
    );
  };

  const mapCreateDtoToApiRequest = (dto: CreateSubscriptionBlockDto): CreateSubscriptionBlockApiRequest => ({
    name: dto.name,
    store_uuids: dto.storeUuids,
    group_invoice: dto.groupInvoice,
  });

  const mapUpdatePaymentMethodDtoToApiRequest = (
    dto: UpdateBlockPaymentMethodDto,
  ): UpdateBlockPaymentMethodApiRequest => ({
    providerId: dto.providerId,
    provider: dto.provider,
  });

  /** The backend accepts snake_case for this specific PATCH body. */
  const mapUpdateInvoiceDataDtoToApiRequest = (dto: UpdateBlockInvoiceDataDto): UpdateBlockInvoiceDataApiRequest => ({
    invoice_data_uuid: dto.invoiceDataUuid,
  });

  const mapUpdateStoresDtoToApiRequest = (dto: UpdateBlockStoresDto): UpdateBlockStoresApiRequest => ({
    name: dto.name,
    store_uuids: dto.storeUuids,
    group_invoice: dto.groupInvoice,
  });

  const mapStoresApiToViewDtos = (apiResponses: StoreApiResponse[]): StoreViewDto[] =>
    apiResponses.map((response) => ({
      uuid: response.uuid,
      name: response.name,
      status: response.status,
      interval: response.interval,
    }));

  return {
    mapApiResponseToEntity,
    mapCreateDtoToApiRequest,
    mapUpdatePaymentMethodDtoToApiRequest,
    mapUpdateInvoiceDataDtoToApiRequest,
    mapUpdateStoresDtoToApiRequest,
    mapStoresApiToViewDtos,
    mapPaymentMethodApiResponseToViewDto,
  };
};
